import { useState } from "react";

import { makePostApiCall } from "../../shared/makeApiCall";
import { useToast } from "../../contexts/ToastContext";

import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import type { EmptyModalState } from "../../components/modals/EmptyModal";
import type { SlideOverState } from "../../components/slideOver/SlideOver";
import type { ApiMessage, SelectOption } from "../../types/api";
import type { CustomerBulkActionPayload, CustomerListItem, CustomersResponse } from "../../types/customers";

export function CustomersViewModel(logout: () => void) {
  const { showSuccess } = useToast();
  const [emptyModal, setEmptyModelOpen] = useState<EmptyModalState>({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<CustomersResponse>({
    current_page: 1, data: [], last_page: 1, per_page: 50, total: 0,
  });
  const [customerGroupOptions, setCustomerGroupOptions] = useState<SelectOption[]>([]);

  const [slideOver, setSlideOver] = useState<SlideOverState>({
    childComponent: null,
    open: false,
  });

  const showErrorModal = (title: string, subtitle?: string) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent setOpen={setEmptyModelOpen} subtitle={subtitle} title={title} />
      ),
      open: true,
    });
  };

  const getCustomers = async function (search: string | null, page: number) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<CustomersResponse, typeof payload>(`customers`, payload);
    setLoading(false);
    if (callResult.success) {
      setCustomers(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message ?? "Could not fetch customers");
    }
  };

  const getCustomerGroupsForDropdown = async function () {
    setLoading(true);

    const callResult = await makePostApiCall<SelectOption[]>(`getCustomersGroupsForDropdown`);
    setLoading(false);
    if (callResult.success) {
      setCustomerGroupOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message ?? "Could not fetch customer groups");
    }
  };

  const onBulkAction = async function (bulkActionValues: CustomerBulkActionPayload) {
    const callResult = await makePostApiCall<ApiMessage, CustomerBulkActionPayload>(`bulkAction/customers`, bulkActionValues);
    if (callResult.success) {
      showSuccess(callResult.data.message || "Bulk action completed");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message ?? "Could not complete bulk action");
    }
  };

  const deleteCustomer = async function (id: number) {
    const callResult = await makePostApiCall<ApiMessage>(`customers/delete/${id}`, {});
    if (callResult.success) {
      showSuccess(callResult.data?.message || "Customer deleted");
      getCustomers(null, 1);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showErrorModal(
          "Could not delete customer",
          callResult.data?.message || "Please try again."
        );
      }
    }
  };

  const askDeleteCustomer = function (customer: CustomerListItem) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          title="Delete customer"
          buttonMessage="Delete"
          message={`Are you sure you want to delete "${customer.company_name || customer.given_name || "this customer"}"? This action cannot be undone.`}
          setOpen={setEmptyModelOpen}
          confirmAction={async () => {
            await deleteCustomer(customer.id);
            setEmptyModelOpen({ childComponent: null, open: false });
          }}
        />
      ),
      open: true,
    });
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    getCustomers,
    loading,
    customers,
    setSlideOver,
    slideOver,
    getCustomerGroupsForDropdown,
    customerGroupOptions,
    onBulkAction,
    askDeleteCustomer,
  };
}
